import React, { useEffect, useRef, useState, useContext } from 'react';
import { Outlet, useLocation, useSearchParams } from 'react-router-dom'; // Outlet renders the matched child route element
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import ThemeSidebar from './ThemeSidebar';
import AmazonSyncBanner from './AmazonSyncBanner';
import styles from './DashboardLayout.module.css';
import useReferralBonus from '../../hooks/useReferralBonus';
import ReferralBonusModal from '../UI/ReferralBonusModal';
import CreditsExhaustedModal from '../UI/CreditsExhaustedModal';
import CatalogueRefreshModal from '../UI/CatalogueRefreshModal';
import AuthContext from '../../context/AuthContext';
import { useAmazonSync } from '../../context/AmazonSyncContext';
import { useFormStep } from '../../context/FormStepContext';
import { useTutorial } from '../../context/TutorialContext';
import { LOW_CREDITS_EVENT } from '../../utils/lowCreditsModal';
import { clearAmazonStatusCache, useAmazonStatus } from '../../hooks/useAmazonStatus';
import { useSingleFlight } from '../../hooks/useSingleFlight';
import { getAmazonConnectUrl, startCatalogueRefresh } from '../../services/authApi';
import { invalidateCatalogue } from '../../utils/catalogueCache';

// Show the low-credits popup when the balance drops below this threshold.
const LOW_CREDITS_THRESHOLD = 50;

const getAuthToken = () => {
  return localStorage.getItem('authToken') ||
         localStorage.getItem('idToken') ||
         localStorage.getItem('refreshToken') ||
         localStorage.getItem('token') ||
         sessionStorage.getItem('token');
};

const DashboardLayout = () => {
  // Use the referral bonus hook
  const { showBonusModal, closeBonusModal, bonusDetails } = useReferralBonus();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { formStep, listingMode, showVariantsTab, isThemesPanelOpen, setIsThemesPanelOpen, showResultPopup, showBothSidebars, setShowBothSidebars, isFontsPanelOpen, setIsFontsPanelOpen } = useFormStep();
  const finalizeCalledRef = useRef(false);
  // Set on the Amazon OAuth return so the refresh popup stays hidden until the
  // next login, when /me reflects the server-side stamp.
  const [connectedThisSession, setConnectedThisSession] = useState(false);

  // Low-credits popup: surfaces the current balance whenever it's below the
  // threshold. Keyed to the balance value (not a single boolean) so every new low
  // value shows once — a lone boolean flag would let the first low popup suppress
  // all later ones (e.g. seeing "0" then never seeing "1"). Zero always re-shows.
  const authCtx = useContext(AuthContext);
  const [showLowCredits, setShowLowCredits] = useState(false);
  // Optional action to run when "Buy Credits" is clicked (e.g. save a draft).
  const [onBuyCredits, setOnBuyCredits] = useState(null);
  // True when the popup was triggered by a feature attempted without enough credits
  // (vs. the passive dashboard balance warning) — switches the modal to "insufficient".
  const [lowCreditsInsufficient, setLowCreditsInsufficient] = useState(false);

  // Connect-Amazon variant of the popup: only for a verified brand-new account —
  // fresh /auth/me this session (not the localStorage cache), reward still
  // pending, never connected, and a zero balance. Anything less falls back to the
  // normal credits copy rather than promising free credits to the wrong user.
  const connectRewardEligible = authCtx?.userVerified === true
    && authCtx?.user?.amazon_connect_credit_pending === true
    && authCtx?.user?.amazon_ever_connected === false
    && authCtx?.user?.credits === 0;

  useEffect(() => {
    // Hold the balance check until /auth/me has answered: the cached user can be
    // stale (e.g. 0 credits right after the Amazon OAuth return) and would
    // otherwise open the wrong popup.
    const credits = authCtx?.user?.credits;
    if (!authCtx?.userVerified || typeof credits !== 'number') return;

    const SHOWN_FOR_KEY = 'lowCreditsShownFor';

    if (credits >= LOW_CREDITS_THRESHOLD) {
      // Balance healthy again — close any open popup and clear so a future dip re-shows.
      sessionStorage.removeItem(SHOWN_FOR_KEY);
      setShowLowCredits(false);
      return;
    }

    // Show for zero on every load (hard block), and once per distinct low value
    // otherwise, so a changed balance is always reflected without nagging on repeats.
    if (credits === 0 || sessionStorage.getItem(SHOWN_FOR_KEY) !== String(credits)) {
      sessionStorage.setItem(SHOWN_FOR_KEY, String(credits));
      setOnBuyCredits(null);
      setLowCreditsInsufficient(false);
      setShowLowCredits(true);
    }
  }, [authCtx?.userVerified, authCtx?.user?.credits]);

  const closeLowCredits = () => {
    setShowLowCredits(false);
    setOnBuyCredits(null);
    setLowCreditsInsufficient(false);
  };

  // New users' free credits arrive on first Amazon connect, so the popup's button
  // sends them straight to Amazon OAuth; on failure the popup stays open to retry.
  const handleConnectAmazon = useSingleFlight(async () => {
    try {
      const authUrl = await getAmazonConnectUrl(authCtx?.token);
      if (authUrl) window.location.href = authUrl;
    } catch (err) {
      console.error('Amazon connect failed:', err);
    }
  });

  // Show the popup when a feature reports a credit-blocked action (e.g. a
  // NO_CREDITS API response) via triggerLowCreditsModal(). An optional
  // onBuyCredits callback (e.g. save draft) runs before navigating to plans.
  useEffect(() => {
    const handler = (e) => {
      const cb = e?.detail?.onBuyCredits;
      setOnBuyCredits(() => (typeof cb === 'function' ? cb : null));
      setLowCreditsInsufficient(true);
      setShowLowCredits(true);
    };
    window.addEventListener(LOW_CREDITS_EVENT, handler);
    return () => window.removeEventListener(LOW_CREDITS_EVENT, handler);
  }, []);

  // One-time Amazon finalize: triggered only when redirected back with ?amazon_connected=true
  useEffect(() => {
    if (searchParams.get('amazon_connected') && !finalizeCalledRef.current) {
      finalizeCalledRef.current = true;

      // Just connected — drop any stale cached "not connected" status so the
      // UI reflects the new connection instead of staying on "Connect Amazon".
      clearAmazonStatusCache();

      // Clean up the URL parameter immediately
      searchParams.delete('amazon_connected');
      setSearchParams(searchParams, { replace: true });

      // Call finalize API once in the background. Connecting fetches the catalogue
      // already, so this also stamps the user as refreshed and the one-off
      // refresh popup is skipped for anyone who connects after login.
      const token = getAuthToken();
      if (token) startCatalogueRefresh(token).catch(() => {});
      setConnectedThisSession(true);
    }
  }, [searchParams, setSearchParams]);
  
  // Check if we're on the infographics tab in single or bulk listing generator
  // Infographics step depends on whether Variants tab is enabled.
  // - Without Variants: Infographics is step 4 (new) / step 5 (existing)
  // - With Variants:    Variants is step 4 (new) / step 5 (existing), Infographics shifts to step 5 (new) / step 6 (existing)
  // Don't show ThemeSidebar when results are showing
  const infographicsStep = showVariantsTab
    ? (listingMode === 'new' ? 5 : 6)
    : (listingMode === 'new' ? 4 : 5);

  const isSingleListingPage = location.pathname.includes('/dashboard/generate/single');
  const isBulkListingPage = location.pathname.includes('/dashboard/generate/bulk');

  const isInfographicsTab =
    !showResultPopup &&
    (isSingleListingPage || isBulkListingPage) &&
    formStep === infographicsStep;
  
  // Close themes panel when results are showing
  useEffect(() => {
    if (showResultPopup && isThemesPanelOpen) {
      setIsThemesPanelOpen(false);
    }
  }, [showResultPopup, isThemesPanelOpen, setIsThemesPanelOpen]);

  // Close fonts panel when results are showing
  useEffect(() => {
    if (showResultPopup && isFontsPanelOpen) {
      setIsFontsPanelOpen(false);
    }
  }, [showResultPopup, isFontsPanelOpen, setIsFontsPanelOpen]);

  // Reset showBothSidebars when navigating away from infographics tab
  useEffect(() => {
    if (!isInfographicsTab && showBothSidebars) {
      setShowBothSidebars(false);
    }
  }, [isInfographicsTab, showBothSidebars, setShowBothSidebars]);

  // Close themes panel when navigating away from infographics tab
  useEffect(() => {
    if (!isInfographicsTab && isThemesPanelOpen) {
      setIsThemesPanelOpen(false);
    }
  }, [isInfographicsTab, isThemesPanelOpen, setIsThemesPanelOpen]);

  // Close fonts panel when navigating away from infographics tab
  useEffect(() => {
    if (!isInfographicsTab && isFontsPanelOpen) {
      setIsFontsPanelOpen(false);
    }
  }, [isInfographicsTab, isFontsPanelOpen, setIsFontsPanelOpen]);
  
  const handleThemesToggle = (isOpen) => {
    setIsThemesPanelOpen(isOpen);
  };

  // One-off blocking catalogue-refresh popup for pre-cutoff accounts whose Amazon
  // account was already connected at login. The server decides eligibility
  // (catalogue_refresh_required); it waits until the tutorial is out of the way.
  const { connected: amazonConnected, loading: checkingAmazonStatus } = useAmazonStatus(authCtx?.token);
  const { refresh: refreshSyncStatus } = useAmazonSync();
  const tutorial = useTutorial();
  const showCatalogueRefresh = authCtx?.user?.catalogue_refresh_required === true
    && amazonConnected && !checkingAmazonStatus && !connectedThisSession
    && !tutorial.introOpen && !tutorial.active;

  const hideCatalogueRefresh = () =>
    authCtx.updateUser({ ...authCtx.user, catalogue_refresh_required: false });

  const handleCatalogueRefresh = useSingleFlight(async () => {
    try {
      await startCatalogueRefresh(authCtx.token);
    } catch (err) {
      // 400 = no usable Amazon connection: nothing to refresh, so release the
      // user instead of trapping them. Server flag is untouched, so the popup
      // re-evaluates on next login. Other errors surface in the modal for retry.
      if (err.status === 400) {
        clearAmazonStatusCache();
        hideCatalogueRefresh();
        return;
      }
      throw err;
    }
    hideCatalogueRefresh();
    invalidateCatalogue();
    refreshSyncStatus();
  });

  return (
    <div className={styles.dashboardLayout}>
      {showBothSidebars && isInfographicsTab ? (
        <>
          <Sidebar showBothSidebars={true} />
          <ThemeSidebar onThemesToggle={handleThemesToggle} isBulk={isBulkListingPage} />
        </>
      ) : isInfographicsTab ? (
        <ThemeSidebar onThemesToggle={handleThemesToggle} isBulk={isBulkListingPage} />
      ) : (
      <Sidebar />
      )}
      <div className={`${styles.mainContent} ${(isThemesPanelOpen || isFontsPanelOpen) && isInfographicsTab ? styles.mainContentWithPanel : ''} ${showBothSidebars && isInfographicsTab ? styles.mainContentWithBothSidebars : ''}`}>
        <Navbar />
        <AmazonSyncBanner />
        <div className={styles.pageContent}>
          <Outlet /> {/* Child routes defined in App.js will render here */}
        </div>
      </div>

      {/* Referral Bonus Modal */}
      <ReferralBonusModal
        show={showBonusModal}
        onClose={closeBonusModal}
        creditsAdded={bonusDetails.creditsAdded}
        tokensAdded={bonusDetails.tokensAdded}
      />

      {/* Low-credits popup (< 50 credits) */}
      <CreditsExhaustedModal
        show={showLowCredits}
        onClose={closeLowCredits}
        onBuyCredits={onBuyCredits}
        credits={authCtx?.user?.credits}
        insufficient={lowCreditsInsufficient}
        connectAmazon={connectRewardEligible}
        connectRewardCredits={authCtx?.user?.amazon_connect_reward_credits}
        onConnectAmazon={handleConnectAmazon}
      />

      <CatalogueRefreshModal isOpen={showCatalogueRefresh} onProceed={handleCatalogueRefresh} />
    </div>
  );
};

export default DashboardLayout;